import { ArrowDown, ArrowUp, ArrowUpRight, Info, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { IndexPath } from '@/Config/app/router/index.path';
import { badgeVariants } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/Domain/components/Shared/Empty/Empty';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { cn } from '@/Domain/utils/cn';

type Props = {
  conversations: number;
  conversationsPercentChange: number;
  conversationsByFormat: Record<string, number>;
  totalMessages: number;
  avgMessagesPerConversation: number;
  messages: number;
  messagesPercentChange: number;
  handoffNow: number;
  handoffPercentChange: number;
  handoffRatePercentChange?: number; // Change in handoff rate (percentage of conversations)
  handoffConversationsByFormat: Record<string, number>;
  isLoading: boolean;
  hasComparison?: boolean;
};

export const KpiSection = ({
  conversations,
  conversationsPercentChange,
  conversationsByFormat,
  totalMessages,
  avgMessagesPerConversation,
  _messages,
  messagesPercentChange,
  handoffNow,
  _handoffPercentChange,
  handoffRatePercentChange,
  handoffConversationsByFormat,
  isLoading,
  hasComparison = true,
}: Props) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();
  const { scenario } = useParams<{ scenario: string }>();

  // isActivated returns false while /subscription is in flight; gate on isFetched so the paywall never flashes at paid users.
  const hasHandoffAccess = isActivated('human_handoff.active');
  const showHandoffPaywall = isSubscriptionFetched && !hasHandoffAccess;
  const isHandoffLoading = isLoading || !isSubscriptionFetched;

  // Calculate handoff rate percentage
  const handoffRate = conversations > 0 ? (handoffNow / conversations) * 100 : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Total Conversations */}
      <Card>
        <CardHeader className="pb-6">
          <CardTitle>{t('dashboard:conversations.conversations')}</CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          {isLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-medium">
                {t('common:number', {
                  value: conversations,
                  formatParams: { value: { maximumFractionDigits: 0 } },
                })}
              </span>
              {hasComparison && <KpiChange value={conversationsPercentChange} />}
            </div>
          )}
        </CardContent>
        <hr className="mx-6 border-border" />
        <CardFooter className="pt-4">
          {isLoading ? (
            <Skeleton className="h-4 w-full" />
          ) : (
            <FormatBreakdown formatData={conversationsByFormat} />
          )}
        </CardFooter>
      </Card>

      {/* AI Messages */}
      <Card>
        <CardHeader className="pb-6">
          <CardTitle>{t('dashboard:conversations.messages')}</CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          {isLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-medium">
                {t('common:number', {
                  value: totalMessages,
                  formatParams: { value: { maximumFractionDigits: 0 } },
                })}
              </span>
              {hasComparison && <KpiChange value={messagesPercentChange} />}
            </div>
          )}
        </CardContent>
        <hr className="mx-6 border-border" />
        <CardFooter className="pt-3.5">
          {isLoading ? (
            <Skeleton className="h-9 w-full rounded-lg" />
          ) : (
            <div className="w-full">
              <div className="flex items-center justify-between rounded-lg bg-accent px-2.5 py-1.5">
                <span className="text-xs text-muted-foreground">
                  {t('dashboard:conversations.avg_per_conversation')}
                </span>
                <span className="text-xs font-semibold text-foreground">
                  {avgMessagesPerConversation.toFixed(1)}
                </span>
              </div>
            </div>
          )}
        </CardFooter>
      </Card>

      {/* Transferred to Human Agent */}
      <Card>
        <CardHeader className="pb-6">
          <div className="flex items-center gap-1.5">
            <CardTitle>{t('dashboard:conversations.transferred_to_human')}</CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  aria-label={t('insights:analytics.handoff_rate_info_aria')}
                  className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Info className="size-3.5 cursor-help text-foreground-subtle transition-colors hover:text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>{t('insights:analytics.handoff_rate_tooltip')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent className="pb-4">
          {isHandoffLoading ? (
            <Skeleton className="h-8 w-24" data-testid="handoff-loading-skeleton" />
          ) : showHandoffPaywall ? (
            <Empty className="md:p-1 gap-0.5">
              <EmptyHeader>
                <EmptyMedia className="size-8" variant="icon">
                  <Lock className="size-4" />
                </EmptyMedia>
                <EmptyTitle className="text-sm">
                  {t('dashboard:conversations.paid_plan_required')}
                </EmptyTitle>
              </EmptyHeader>
              <EmptyContent className="mt-3">
                <Button variant="dark" size="sm" asChild>
                  <Link to={`/i/${scenario}/${IndexPath.subscription}`}>
                    {t('dashboard:conversations.link_billing')}{' '}
                    <ArrowUpRight className="text-sm size-4" />
                  </Link>
                </Button>
              </EmptyContent>
            </Empty>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-medium">
                {t('common:number', {
                  value: handoffRate,
                  formatParams: { value: { maximumFractionDigits: 1 } },
                })}
                %
              </span>
              {hasComparison && (
                <KpiChange
                  value={handoffRatePercentChange ?? 0}
                  isPercentagePoints={true}
                  invertColors={true}
                />
              )}
              <span className="mb-px inline-flex self-end text-xs text-muted-foreground">
                (
                {t('common:number', {
                  value: handoffNow,
                  formatParams: { value: { maximumFractionDigits: 0 } },
                })}
                )
              </span>
            </div>
          )}
        </CardContent>
        {isHandoffLoading || !showHandoffPaywall ? (
          <>
            <hr className="mx-6 border-border" />
            <CardFooter className="pt-4">
              {isHandoffLoading ? (
                <Skeleton className="h-4 w-full" />
              ) : (
                <FormatBreakdown
                  formatData={handoffConversationsByFormat}
                  totalLabel={handoffNow}
                  labelTotalsByFormat={conversationsByFormat}
                />
              )}
            </CardFooter>
          </>
        ) : null}
      </Card>
    </div>
  );
};

const KpiChange = ({
  value,
  isPercentagePoints = false,
  invertColors = false,
}: {
  value: number;
  isPercentagePoints?: boolean;
  invertColors?: boolean;
}) => {
  const { t } = useTranslation(['common', 'insights']);
  const isUp = value > 0;
  const isDown = value < 0;

  // Normal colors: green=good/up, red=bad/down
  // Inverted colors: red=bad/up, green=good/down (for metrics where increase is bad)
  const isPositive = invertColors ? isDown : isUp;
  const isNegative = invertColors ? isUp : isDown;

  const variant = isPositive ? 'success' : isNegative ? 'error' : 'secondary';
  const IconComponent = isUp ? ArrowUp : isDown ? ArrowDown : null;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          data-testid="kpi-change"
          className={cn(
            badgeVariants({ variant, size: 'md' }),
            'cursor-help',
            'focus:ring-0 focus:ring-offset-0',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        >
          {IconComponent && <IconComponent className="h-3.5 w-3.5" />}
          {t('common:number', {
            value: value,
            formatParams: { value: { maximumFractionDigits: 1 } },
          })}
          {isPercentagePoints ? 'pp' : '%'}
        </TooltipTrigger>
        <TooltipContent>{t('insights:analytics.change_tooltip')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const colorClasses: Record<string, string> = {
  chat: 'bg-foreground-subtle/60',
  email: 'bg-foreground-muted',
};

const FormatBreakdown = ({
  formatData,
  totalLabel,
  labelTotalsByFormat,
}: {
  formatData: Record<string, number>;
  totalLabel?: number;
  labelTotalsByFormat?: Record<string, number>;
}) => {
  const { t } = useTranslation(['dashboard']);

  const chatCount = formatData.chat ?? 0;
  const emailCount = formatData.email ?? 0;

  // Total for bar visualization (sum of actual values)
  const barTotal = chatCount + emailCount;
  // Total for percentage label (can be overridden per format for rate calculations)
  const labelTotal = totalLabel ?? barTotal;
  const chatLabelTotal = labelTotalsByFormat?.chat ?? labelTotal;
  const emailLabelTotal = labelTotalsByFormat?.email ?? labelTotal;

  const data = [
    {
      label: t('dashboard:conversations.chat'),
      count: chatCount,
      barPercentage: barTotal > 0 ? (chatCount / barTotal) * 100 : 0,
      labelPercentage: chatLabelTotal > 0 ? (chatCount / chatLabelTotal) * 100 : 0,
      color: 'chat',
    },
    {
      label: t('dashboard:conversations.email'),
      count: emailCount,
      barPercentage: barTotal > 0 ? (emailCount / barTotal) * 100 : 0,
      labelPercentage: emailLabelTotal > 0 ? (emailCount / emailLabelTotal) * 100 : 0,
      color: 'email',
    },
  ];

  // Filter only items with count > 0 for the bar visualization
  const barData = data.filter((item) => item.count > 0);

  if (barTotal === 0) {
    return <p className="text-sm text-muted-foreground">{t('dashboard:conversations.no_data')}</p>;
  }

  return (
    <div className="w-full mt-0.5">
      <div className="flex items-center gap-0.5 w-full">
        {barData.map((item, index) => (
          <div
            key={index}
            className={`${colorClasses[item.color]} h-1.5 rounded`}
            style={{ width: `${item.barPercentage}%` }}
          />
        ))}
      </div>

      <ul role="list" className="mt-3.5 space-y-1.5">
        {data.map((item, index) => (
          <li key={index} className="flex items-center gap-2 text-xs">
            <span
              className={`${colorClasses[item.color]} size-2.5 rounded-full`}
              aria-hidden="true"
            />
            <span className="text-foreground">{item.label}</span>
            <span className="text-muted-foreground">
              ({item.count} / {item.labelPercentage.toFixed(1)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
